import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  Typography,
  Avatar,
  Chip,
  CircularProgress,
  Alert
} from '@mui/material';
import { Business, ArrowForward } from '@mui/icons-material';
import { useAuth } from '../hooks/useAuth';
import authService from '../services/authService';

interface AssetOwner {
  id: number;
  name: string;
  code: string;
  sites_count: number;
  user_role: string;
  logo_url?: string;
}

interface AssetOwnerSelectionProps {
  onAssetOwnerSelect: (assetOwnerId: number, assetOwnerName: string, assetOwnerLogoUrl?: string) => void;
}

const AssetOwnerSelection: React.FC<AssetOwnerSelectionProps> = ({ onAssetOwnerSelect }) => {
  const { user, isAuthenticated, isLoading: authLoading } = useAuth();
  const [assetOwners, setAssetOwners] = useState<AssetOwner[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchAssetOwners = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('🔍 AssetOwnerSelection: Starting fetch with state:', {
        isAuthenticated,
        authLoading,
        hasUser: !!user,
        hasSiteRoles: !!user?.site_roles,
        siteRolesCount: user?.site_roles?.length || 0,
        username: user?.username
      });

      // Try to fetch asset owners directly from API first
      console.log('🌐 AssetOwnerSelection: Fetching asset owners from API...');
      const response = await fetch('/api/v1/sites/asset-owners/my_asset_owners/', {
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      console.log('📡 AssetOwnerSelection: API response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ AssetOwnerSelection: API error:', response.status, errorText);
        
        // If it's an authentication error, validate the session
        if (response.status === 401) {
          console.log('🔍 AssetOwnerSelection: Authentication error, validating session...');
          const sessionValid = await authService.validateSession();
          console.log('🔍 AssetOwnerSelection: Session validation result:', sessionValid);
        }
        
        throw new Error(`Failed to fetch asset owners: ${response.status} ${response.statusText}`);
      }

      const apiAssetOwners = await response.json();
      console.log('📦 AssetOwnerSelection: API returned asset owners:', apiAssetOwners);
      console.log('📦 AssetOwnerSelection: API returned type:', typeof apiAssetOwners);
      console.log('📦 AssetOwnerSelection: API returned is array:', Array.isArray(apiAssetOwners));
      
      if (!Array.isArray(apiAssetOwners)) {
        console.error('❌ AssetOwnerSelection: API did not return an array! Got:', apiAssetOwners);
        
        // Check if it's a pagination response with results
        if (apiAssetOwners && typeof apiAssetOwners === 'object' && Array.isArray(apiAssetOwners.results)) {
          console.log('📦 AssetOwnerSelection: Found paginated results, using results array');
          const paginatedResults = apiAssetOwners.results;
          console.log('📦 AssetOwnerSelection: Paginated results count:', paginatedResults.length);
          
          // Continue with the paginated results
          if (user?.site_roles && user.site_roles.length > 0) {
            console.log('📊 AssetOwnerSelection: Using site roles to filter asset owners');
            
            // Get unique asset owner IDs from user's site roles
            const uniqueAssetOwnerIds = Array.from(new Set(
              user.site_roles
                .filter((role: any) => role.is_active)
                .map((role: any) => role.asset_owner_id)
            )) as number[];

            console.log('📊 AssetOwnerSelection: Asset owner IDs from site roles:', uniqueAssetOwnerIds);

            // Filter the API results to only include asset owners the user has roles for
            const filteredAssetOwners = paginatedResults.filter((ao: any) => 
              uniqueAssetOwnerIds.indexOf(ao.id) !== -1
            );

            console.log('📊 AssetOwnerSelection: Filtered asset owners count:', filteredAssetOwners.length);

            // Map the filtered results with role information
            const assetOwnersWithRoles = filteredAssetOwners.map((assetOwner: any) => {
              // Get the user's roles for this asset owner
              const userRoles = (user?.site_roles || []).filter((role: any) => 
                role.asset_owner_id === assetOwner.id && role.is_active
              );

              console.log(`📊 AssetOwnerSelection: Roles for ${assetOwner.name}:`, userRoles.map((r: any) => r.role));

              return {
                ...assetOwner,
                user_role: userRoles.length > 0 ? userRoles[0].role : 'unknown'
              };
            });

            console.log('📊 AssetOwnerSelection: Final asset owners with roles:', assetOwnersWithRoles);
            setAssetOwners(assetOwnersWithRoles);
          } else {
            console.log('📊 AssetOwnerSelection: No site roles found, using all paginated results');
            setAssetOwners(paginatedResults);
          }
          return;
        }
        
        // If not a pagination response, throw an error
        throw new Error(`API did not return an array or paginated results. Got: ${typeof apiAssetOwners}`);
      }
      
      console.log('📦 AssetOwnerSelection: API returned asset owners count:', apiAssetOwners.length);

      // If we have user site roles, filter and add role information
      if (user?.site_roles && user.site_roles.length > 0) {
        console.log('📊 AssetOwnerSelection: Using site roles to filter asset owners');
        
        // Get unique asset owner IDs from user's site roles
        const uniqueAssetOwnerIds = Array.from(new Set(
          user.site_roles
            .filter(role => role.is_active)
            .map(role => role.asset_owner_id)
        ));

        console.log('📊 AssetOwnerSelection: Asset owner IDs from site roles:', uniqueAssetOwnerIds);

        // Filter to only include asset owners the user has access to
        const userAssetOwners = apiAssetOwners.filter((owner: any) => 
          uniqueAssetOwnerIds.indexOf(owner.id) !== -1
        );

        console.log('🔒 AssetOwnerSelection: Filtered user asset owners:', userAssetOwners.length);

        // Add user role information from site roles
        const assetOwnersWithRoles = userAssetOwners.map((owner: any) => {
          const userRoles = user.site_roles
            ? user.site_roles
                .filter(role => role.asset_owner_id === owner.id && role.is_active)
                .map(role => role.role)
            : [];
          
          return {
            ...owner,
            user_role: userRoles[0] || 'user', // Show first role
            sites_count: owner.sites_count || 0
          };
        });

        console.log('✅ AssetOwnerSelection: Final asset owners with roles:', assetOwnersWithRoles);
        setAssetOwners(assetOwnersWithRoles);
      } else {
        // Fallback: use all asset owners returned by API (API should handle filtering)
        console.log('⚠️ AssetOwnerSelection: No site roles found, using all API asset owners');
        const formattedAssetOwners = apiAssetOwners.map((owner: any) => ({
          ...owner,
          user_role: 'user',
          sites_count: owner.sites_count || 0
        }));
        
        console.log('✅ AssetOwnerSelection: Using fallback asset owners:', formattedAssetOwners.length);
        setAssetOwners(formattedAssetOwners);
      }
    } catch (err) {
      console.error('❌ AssetOwnerSelection: Error fetching asset owners:', err);
      setError(`Failed to load asset owners: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  }, [user, isAuthenticated, authLoading]);

  useEffect(() => {
    console.log('🔄 AssetOwnerSelection: useEffect triggered with:', {
      isAuthenticated,
      authLoading,
      hasUser: !!user,
      hasSiteRoles: !!user?.site_roles,
      siteRolesCount: user?.site_roles?.length || 0
    });

    // Only fetch asset owners if user is authenticated and auth is not loading
    if (isAuthenticated && !authLoading && user) {
      console.log('✅ AssetOwnerSelection: User authenticated, fetching asset owners...');
      fetchAssetOwners();
    } else {
      console.log('⏳ AssetOwnerSelection: Waiting for authentication to complete...');
    }
  }, [user, isAuthenticated, authLoading, fetchAssetOwners]);

  if (loading || authLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <CircularProgress />
        <Typography variant="h6" ml={2}>
          {authLoading ? 'Loading authentication...' : 'Loading asset owners...'}
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 3 }}>
        {error}
      </Alert>
    );
  }

  if (assetOwners.length === 0) {
    return (
      <Alert severity="warning" sx={{ mb: 3 }}>
        No asset owners found. Please contact your administrator to assign you to sites.
      </Alert>
    );
  }

  return (
    <Box>
      <Box mb={4}>
        <Typography variant="h4" component="h1" gutterBottom>
          Select Asset Owner
        </Typography>
        <Typography variant="subtitle1" color="text.secondary">
          Choose an asset owner to access their generation operations
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {assetOwners.map((assetOwner) => (
          <Grid item xs={12} sm={6} md={4} key={assetOwner.id}>
            <Card 
              sx={{ 
                height: '100%',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 6
                }
              }}
            >
              <CardActionArea 
                onClick={() => {
                  onAssetOwnerSelect(assetOwner.id, assetOwner.name, assetOwner.logo_url);
                }}
                sx={{ height: '100%', p: 2 }}
              >
                <CardContent sx={{ textAlign: 'center' }}>
                  <Avatar
                    sx={{ 
                      width: 80, 
                      height: 80, 
                      margin: '0 auto 16px',
                      bgcolor: assetOwner.logo_url ? 'transparent' : 'primary.main',
                      fontSize: '1.5rem'
                    }}
                    src={assetOwner.logo_url || undefined}
                  >
                    {!assetOwner.logo_url && <Business sx={{ fontSize: 40 }} />}
                  </Avatar>
                  
                  <Typography variant="h5" component="h2" gutterBottom>
                    {assetOwner.name}
                  </Typography>
                  
                  <Typography variant="body2" color="text.secondary" mb={2}>
                    {assetOwner.sites_count} site{assetOwner.sites_count !== 1 ? 's' : ''} available
                  </Typography>
                  
                  <Box display="flex" justifyContent="center" gap={1} mb={2}>
                    <Chip 
                      label={assetOwner.user_role} 
                      color="primary" 
                      size="small" 
                    />
                  </Box>

                  <Box display="flex" alignItems="center" justifyContent="center" mt={2}>
                    <Typography variant="body2" color="primary">
                      Access Operations
                    </Typography>
                    <ArrowForward color="primary" sx={{ ml: 1 }} />
                  </Box>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default AssetOwnerSelection; 